import { useContext, useMemo } from "react";
import useNavigationTitle from "components/navigation/useNavigationTitle";
import usePluginEntities from "hooks/usePluginEntities";
import { PAGE_TYPE, ACTION_TYPE, LINK_TYPE } from "components/quick-jump/Constants";
import usePermissions from "hooks/usePermissions";
import Routes, { prefixUrl } from "routing/Routes";
import AppConfig from "util/AppConfig";
import useCurrentUser from "hooks/useCurrentUser";
import { ScratchpadContext } from "contexts/ScratchpadProvider";
import { isTelemetryDebugEnabled, setTelemetryDebugEnabled } from "logic/telemetry/debug/TelemetryDebugStore";
const isFeatureEnabled = (featureFlag) => {
  if (!featureFlag) return true;
  return AppConfig.isFeatureEnabled(featureFlag);
};
const useEntityCreatorItems = () => {
  const { isPermitted } = usePermissions();
  const entityCreators = usePluginEntities("entityCreators");
  return entityCreators.filter((creator) => creator.permissions ? isPermitted(creator.permissions) : true).filter((creator) => isFeatureEnabled(creator.requiredFeatureFlag)).map((creator) => ({ type: PAGE_TYPE, link: creator.path, title: creator.title }));
};
const useConfigurationPages = () => {
  const { isPermitted } = usePermissions();
  const coreSystemConfigurations = usePluginEntities("coreSystemConfigurations");
  const pluginSystemConfigurations = usePluginEntities("systemConfigurations");
  const coreNavItems = coreSystemConfigurations.filter(({ permissions }) => isPermitted(permissions)).map((page) => ({
    type: PAGE_TYPE,
    link: prefixUrl(`${Routes.SYSTEM.CONFIGURATIONS}/${page.name}`),
    title: `Configurations / ${page.name}`
  }));
  const pluginNavItems = pluginSystemConfigurations.filter(({ useCondition }) => {
    var _a;
    return (_a = useCondition == null ? void 0 : useCondition()) != null ? _a : true;
  }).map((page) => ({
    type: PAGE_TYPE,
    link: prefixUrl(`${Routes.SYSTEM.configurationsSection("Plugins", page.configType)}`),
    title: `Configurations / ${page.displayName}`
  }));
  return [...coreNavItems, ...pluginNavItems];
};
const useQuickJumpActions = () => {
  const { isScratchpadVisible } = useContext(ScratchpadContext);
  return [
    {
      type: ACTION_TYPE,
      title: "Logout current user",
      action: ({ logout }) => {
        logout();
      }
    },
    {
      type: ACTION_TYPE,
      title: "Toggle Theme",
      action: ({ toggleThemeMode }) => {
        toggleThemeMode();
      }
    },
    {
      type: ACTION_TYPE,
      title: `${isScratchpadVisible ? "Hide" : "Show"} Scratchpad`,
      action: ({ toggleScratchpad }) => {
        toggleScratchpad();
      }
    },
    // The overlay itself works in any build (localStorage flag); only this discoverable entry
    // point is limited to development mode.
    ...AppConfig.gl2DevMode() ? [
      {
        type: ACTION_TYPE,
        title: `${isTelemetryDebugEnabled() ? "Disable" : "Enable"} Telemetry Debug Overlay`,
        action: () => {
          setTelemetryDebugEnabled(!isTelemetryDebugEnabled());
        }
      }
    ] : []
  ];
};
const useQuickJumpLinks = () => {
  const { readOnly, id: userId } = useCurrentUser();
  return [
    {
      type: PAGE_TYPE,
      title: `${readOnly ? "Show" : "Edit"} profile for current user`,
      link: readOnly ? Routes.SYSTEM.USERS.show(userId) : Routes.SYSTEM.USERS.edit(userId)
    },
    {
      type: PAGE_TYPE,
      title: "Welcome",
      link: Routes.WELCOME
    }
  ];
};
const useHelpMenuItems = () => {
  const menuItems = usePluginEntities("helpMenu");
  const navigationTitle = useNavigationTitle();
  const { isPermitted } = usePermissions();
  return menuItems.filter((item) => isPermitted(item.permissions)).map((item) => {
    if ("externalLink" in item) {
      return {
        type: LINK_TYPE,
        externalLink: item.externalLink,
        title: navigationTitle(item)
      };
    }
    if ("action" in item) {
      return {
        type: ACTION_TYPE,
        title: navigationTitle(item),
        action: item.action
      };
    }
    if ("path" in item) {
      return {
        type: PAGE_TYPE,
        title: navigationTitle(item),
        link: prefixUrl(item.path)
      };
    }
    throw Error("Help menu item must have either external link or action defined");
  });
};
const useMainNavigationItems = () => {
  const { isPermitted } = usePermissions();
  const navigationItems = usePluginEntities("navigation");
  const navigationTitle = useNavigationTitle();
  const allNavigationItems = navigationItems.flatMap((item) => {
    var _a, _b;
    const itemVisible = ((_b = (_a = item.useCondition) == null ? void 0 : _a.call(item)) != null ? _b : true) && isFeatureEnabled(item.requiredFeatureFlag) && isPermitted(item.permissions);
    const visibleChildren = "children" in item ? item.children.filter(
      (child) => {
        var _a2, _b2;
        return ((_b2 = (_a2 = child.useCondition) == null ? void 0 : _a2.call(child)) != null ? _b2 : true) && isFeatureEnabled(child.requiredFeatureFlag);
      }
    ) : [];
    if (!itemVisible) {
      return [];
    }
    if (!("children" in item)) {
      return [{ ...item, title: navigationTitle(item) }];
    }
    return visibleChildren.map((child) => ({
      ...child,
      title: `${navigationTitle(item)} / ${navigationTitle(child)}`
    }));
  });
  return allNavigationItems.filter((item) => isPermitted(item.permissions)).map((item) => ({ type: PAGE_TYPE, link: item.path, title: item.title }));
};
const usePageNavigationItems = () => {
  const { isPermitted } = usePermissions();
  const pageNavigationItems = usePluginEntities("pageNavigation");
  const navigationTitle = useNavigationTitle();
  return pageNavigationItems.flatMap(
    (group) => [...group.children].filter(
      ({ useCondition, requiredFeatureFlag }) => {
        var _a;
        return (
          // eslint-disable-next-line react-hooks/rules-of-hooks
          ((_a = useCondition == null ? void 0 : useCondition()) != null ? _a : true) && isFeatureEnabled(requiredFeatureFlag)
        );
      }
    ).filter((page) => isPermitted(page.permissions)).slice(1).map((page) => ({
      type: PAGE_TYPE,
      link: page.path,
      title: `${navigationTitle(group)} / ${navigationTitle(page)}`
    }))
  );
};
const useNavItems = () => {
  const mainNavItems = useMainNavigationItems();
  const pageNavItems = usePageNavigationItems();
  const creatorItems = useEntityCreatorItems();
  const configurationPageNavItems = useConfigurationPages();
  const quickJumpActions = useQuickJumpActions();
  const quickJumpLinks = useQuickJumpLinks();
  const helpMenuItems = useHelpMenuItems();
  return useMemo(
    () => [
      ...mainNavItems,
      ...pageNavItems,
      ...creatorItems,
      ...configurationPageNavItems,
      ...quickJumpActions,
      ...quickJumpLinks,
      ...helpMenuItems
    ],
    [
      configurationPageNavItems,
      creatorItems,
      helpMenuItems,
      mainNavItems,
      pageNavItems,
      quickJumpActions,
      quickJumpLinks
    ]
  );
};
export default useNavItems;
